import React, { useEffect, useState } from "react";
import { useApp } from "@/context/AppContext";
import api, { apiError } from "@/lib/api";
import { PageHeader, EmptyState, TableSkeleton, StatusBadge } from "@/components/common/Common";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Users, Plus, MoreHorizontal, Pencil, Power, KeyRound, Trash2, Download, Search } from "lucide-react";

const empty = {
  first_name: "", last_name: "", username: "", email: "", phone: "", password: "Demo@1234",
  role_ids: [], branch_ids: [], status: "active", language: "en", theme: "light",
};

export default function UsersPage() {
  const { t, can, user: me } = useApp();
  const [users, setUsers] = useState(null);
  const [roles, setRoles] = useState([]);
  const [branches, setBranches] = useState([]);
  const [search, setSearch] = useState("");
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState(empty);
  const [confirmPw, setConfirmPw] = useState("Demo@1234");
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null);

  const load = async () => {
    try {
      const [u, r, b] = await Promise.all([api.get("/users"), api.get("/roles").catch(() => ({ data: [] })), api.get("/branches").catch(() => ({ data: [] }))]);
      setUsers(u.data); setRoles(r.data); setBranches(b.data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(empty); setConfirmPw("Demo@1234"); setDialog(true); };
  const openEdit = (u) => { setEditing(u); setForm({ ...empty, ...u }); setDialog(true); };

  const save = async () => {
    if (!editing && form.password !== confirmPw) return toast.error("Passwords do not match.");
    setSaving(true);
    try {
      if (editing) {
        const { password, ...payload } = form;
        await api.put(`/users/${editing.id}`, payload); toast.success("User updated");
      } else { await api.post("/users", form); toast.success("User created"); }
      setDialog(false); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const toggleStatus = async (u) => {
    try { await api.patch(`/users/${u.id}/status`); toast.success("Status updated"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const resetPw = async (u) => {
    try { const { data } = await api.post(`/users/${u.id}/reset-password`, {}); toast.success(`Password reset to: ${data.new_password}`); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const doDelete = async (u) => {
    try { await api.delete(`/users/${u.id}`); toast.success("User deleted"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setConfirm(null);
  };
  const exportCsv = () => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/users/export`, "_blank");

  const toggleArr = (field, id) => setForm((f) => ({ ...f, [field]: f[field].includes(id) ? f[field].filter((x) => x !== id) : [...f[field], id] }));

  const filtered = (users || []).filter((u) =>
    !search || `${u.first_name} ${u.last_name} ${u.email} ${u.username}`.toLowerCase().includes(search.toLowerCase()));

  return (
    <div>
      <PageHeader title={t("userManagement")} subtitle={t("users")}>
        {can("reports.export") && <Button variant="outline" size="sm" className="gap-2" onClick={exportCsv} data-testid="users-export-btn"><Download className="h-4 w-4" />{t("export")}</Button>}
        {can("users.create") && <Button size="sm" className="gap-2" onClick={openCreate} data-testid="create-user-btn"><Plus className="h-4 w-4" />{t("createUser")}</Button>}
      </PageHeader>

      <div className="bg-card border border-border rounded-md">
        <div className="p-4 border-b border-border">
          <div className="relative max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("search")} className="pl-9" data-testid="users-search" />
          </div>
        </div>
        {users === null ? <TableSkeleton cols={6} /> : filtered.length === 0 ? (
          <EmptyState icon={Users} title={t("noUsers")}
            action={can("users.create") && <Button size="sm" className="gap-2" onClick={openCreate}><Plus className="h-4 w-4" />{t("createUser")}</Button>} />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("name")}</TableHead>
                  <TableHead>{t("email")}</TableHead>
                  <TableHead>{t("role")}</TableHead>
                  <TableHead>{t("branch")}</TableHead>
                  <TableHead>{t("status")}</TableHead>
                  <TableHead>{t("lastLogin")}</TableHead>
                  <TableHead className="text-right">{t("actions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((u) => (
                  <TableRow key={u.id} data-testid={`user-row-${u.username}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="h-8 w-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold">{u.first_name[0]}{u.last_name[0]}</div>
                        <div><p className="font-medium">{u.first_name} {u.last_name}</p><p className="text-xs text-muted-foreground">@{u.username}</p></div>
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{u.email}</TableCell>
                    <TableCell><div className="flex flex-wrap gap-1">{u.role_names?.map((r) => <Badge key={r} variant="secondary" className="text-[10px]">{r}</Badge>)}</div></TableCell>
                    <TableCell className="text-xs text-muted-foreground">{u.branch_names?.join(", ") || "—"}</TableCell>
                    <TableCell><StatusBadge status={u.status} /></TableCell>
                    <TableCell className="text-xs text-muted-foreground">{u.last_login ? new Date(u.last_login).toLocaleDateString() : "Never"}</TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" data-testid={`user-actions-${u.username}`}><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {can("users.edit") && <DropdownMenuItem onClick={() => openEdit(u)} data-testid={`user-edit-${u.username}`}><Pencil className="h-4 w-4 mr-2" />{t("edit")}</DropdownMenuItem>}
                          {can("users.reset_password") && <DropdownMenuItem onClick={() => resetPw(u)} data-testid={`user-reset-${u.username}`}><KeyRound className="h-4 w-4 mr-2" />{t("resetPassword")}</DropdownMenuItem>}
                          {can("users.disable") && u.id !== me.id && <DropdownMenuItem onClick={() => toggleStatus(u)} data-testid={`user-toggle-${u.username}`}><Power className="h-4 w-4 mr-2" />{u.status === "active" ? t("disable") : t("enable")}</DropdownMenuItem>}
                          {can("users.delete") && u.id !== me.id && <DropdownMenuItem className="text-destructive" onClick={() => setConfirm(u)} data-testid={`user-delete-${u.username}`}><Trash2 className="h-4 w-4 mr-2" />{t("delete")}</DropdownMenuItem>}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? t("editUser") : t("createUser")}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>{t("firstName")} *</Label><Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="mt-1.5" data-testid="user-firstname-input" /></div>
            <div><Label>{t("lastName")} *</Label><Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="mt-1.5" data-testid="user-lastname-input" /></div>
            <div><Label>{t("username")} *</Label><Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} className="mt-1.5" data-testid="user-username-input" /></div>
            <div><Label>{t("email")} *</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" data-testid="user-email-input" /></div>
            <div><Label>{t("phone")}</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5" data-testid="user-phone-input" /></div>
            <div><Label>{t("status")}</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="mt-1.5" data-testid="user-status-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="active">{t("active")}</SelectItem><SelectItem value="inactive">{t("inactive")}</SelectItem></SelectContent>
              </Select>
            </div>
            {!editing && <>
              <div><Label>{t("password")} *</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1.5" data-testid="user-password-input" /></div>
              <div><Label>{t("confirmPassword")} *</Label><Input type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} className="mt-1.5" data-testid="user-confirm-input" /></div>
            </>}
          </div>
          <div className="grid grid-cols-2 gap-4 mt-2">
            <div>
              <Label className="mb-2 block">{t("assignRole")} *</Label>
              <div className="border border-border rounded-md p-3 space-y-2 max-h-40 overflow-y-auto">
                {roles.map((r) => (
                  <label key={r.id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={form.role_ids.includes(r.id)} onCheckedChange={() => toggleArr("role_ids", r.id)} data-testid={`user-role-${r.name}`} />
                    {r.name}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <Label className="mb-2 block">{t("assignBranch")} *</Label>
              <div className="border border-border rounded-md p-3 space-y-2 max-h-40 overflow-y-auto">
                {branches.map((b) => (
                  <label key={b.id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={form.branch_ids.includes(b.id)} onCheckedChange={() => toggleArr("branch_ids", b.id)} data-testid={`user-branch-${b.code}`} />
                    {b.name}
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>{t("cancel")}</Button>
            <Button onClick={save} disabled={saving || !form.first_name || !form.email || !form.username || form.role_ids.length === 0 || form.branch_ids.length === 0} data-testid="user-save-btn">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={!!confirm} onOpenChange={() => setConfirm(null)} destructive
        title="Delete user?" description={`Are you sure you want to delete ${confirm?.first_name} ${confirm?.last_name}?`}
        confirmLabel={t("delete")} onConfirm={() => doDelete(confirm)} />
    </div>
  );
}
